import React, { useState, useEffect } from 'react';
import { Layout, Typography, Table, DatePicker, Input, Row, Col } from 'antd';
import { getRequest } from '../utilities/apicalls';
import ToastMessage from '../components/ToastMessage';
import AdminSidebar from '../components/AdminSidebar';
import '../styles/AdminReports.css';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const AdminReports = () => {
  const [loading, setLoading] = useState(false);
  const [allScholars, setAllScholars] = useState([]);
  const [filteredScholars, setFilteredScholars] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const fetchScholars = async (dates = dateRange) => {
    try {
      setLoading(true);

      const params = {};
      if (dates && dates.length === 2) {
        params.date_from = dates[0].format('YYYY-MM-DD');
        params.date_to = dates[1].format('YYYY-MM-DD');
      }

      const response = await getRequest('scholars/get_scholar_total_allowance', params);

      if (response.success && Array.isArray(response.data?.data)) {
        const scholars = response.data.data.map((item) => ({
          key: item.id,
          scholar: item.scholar.trim(),
          total_allowance: item.total_allowance,
          total_receipts_signed: item.total_receipts_signed,
          total_receipts: item.total_receipts,
          scholar_type: item.scholar_type || 'N/A',
          course: item.course || 'N/A',
        }));

        setAllScholars(scholars);
        setFilteredScholars(scholars);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      console.error('Error fetching scholars:', error);
      ToastMessage.error('Failed to load scholar reports. Please try again.');
      setAllScholars([]);
      setFilteredScholars([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScholars();
  }, []);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    fetchScholars(dates);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    const filtered = allScholars.filter((item) =>
      item.scholar.toLowerCase().includes(value)
    );
    setFilteredScholars(filtered);
  };

  const columns = [
    {
      title: 'Scholar Name',
      dataIndex: 'scholar',
      key: 'scholar',
      align: 'left',
    },
    {
      title: 'Total Receipts Signed',
      dataIndex: 'total_receipts_signed',
      key: 'total_receipts_signed',
      align: 'left',
      render: (text, record) => `${record.total_receipts_signed} / ${record.total_receipts}`,
    },
    {
      title: 'Total Allowance',
      dataIndex: 'total_allowance',
      key: 'total_allowance',
      align: 'right',
      render: (value) => `₱${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`,
    },
  ];

  return (
    <Layout className="admin-layout">
      <AdminSidebar
        activePage="report"
        onCollapse={(collapsed) => setSidebarCollapsed(collapsed)}
      />
      <Content className={`admin-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="admin-scholar-container">
          <Title level={4}>Report</Title>

          {/* Filters */}
          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            {/* Date Picker */}
            <Col span={6}>
              <RangePicker
                className="date-range-filter"
                placeholder={['Date From', 'Date To']}
                value={dateRange || null}
                onChange={handleDateRangeChange}
                allowClear
              />
            </Col>

            {/* Search by Name */}
            <Col span={6}>
              <Input
                className="scholar-search"
                placeholder="Search by Name"
                value={searchText || ''}
                onChange={handleSearchChange}
                allowClear
              />
            </Col>
          </Row>

          {/* Scholars Table */}
          <Table
            className="scholars-table"
            columns={columns}
            dataSource={filteredScholars}
            rowKey="key"
            loading={loading}
            pagination={{
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              pageSize: pageSize,
              total: filteredScholars.length,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, filteredScholars.length.toString()],
            }}
          />
        </div>
      </Content>
    </Layout>
  );
};

export default AdminReports;
